// One curtain, for a component: the three actions, the live run, and the
// configured travel times.
//
// The state and the timers live in core/devices/curtainRunner — this only
// subscribes. Both shells and the grid card use this hook, so a curtain shows
// the same countdown wherever it appears.

import { useCallback, useEffect, useState } from 'react';
import { useApp } from '../state/AppContext';
import {
  readCurtainTimes, saveCurtainTimes, DEFAULT_TRAVEL_S, curtainNoun,
} from '../devices/curtain';
import {
  subscribeCurtains, curtainState, openCurtain, closeCurtain, stopCurtain,
  IDLE, OPENING, CLOSING,
} from '../devices/curtainRunner';

export { IDLE, OPENING, CLOSING };

/** How often the countdown redraws. 10 fps — smooth enough for a 1 s run. */
const TICK_MS = 100;

export default function useCurtain(device) {
  const { userId } = useApp();
  const uno = device?.UniqueNumber ?? device?.uniqueNumber;

  // "Curtain" or "Gate" — the controls are identical, the words are not.
  const noun = curtainNoun(device?.DeviceID ?? device?.DeviceId);

  const [times, setTimes] = useState({
    openSeconds: DEFAULT_TRAVEL_S,
    closeSeconds: DEFAULT_TRAVEL_S,
    openSet: false,
    closeSet: false,
    singleOutput: false,
  });
  const [loadingTimes, setLoadingTimes] = useState(true);
  const [run, setRun] = useState(() => curtainState(uno));
  const [now, setNow] = useState(() => Date.now());

  // The configured seconds. Read once per device — they change only when
  // somebody edits them, and `reloadTimes` covers that.
  const reloadTimes = useCallback(async () => {
    if (!uno) return;
    setLoadingTimes(true);
    const t = await readCurtainTimes(uno);
    setTimes(t);
    setLoadingTimes(false);
  }, [uno]);

  useEffect(() => { reloadTimes(); }, [reloadTimes]);

  // The run. Re-read on every runner event rather than diffed — there is one
  // curtain on screen and the object is four numbers.
  useEffect(() => {
    setRun(curtainState(uno));
    return subscribeCurtains(() => setRun(curtainState(uno)));
  }, [uno]);

  // ⚠️ THE TICKER ONLY RUNS WHILE THE CURTAIN DOES. A 100 ms interval left
  // running on every idle curtain card in a room would re-render the whole grid
  // ten times a second for nothing.
  const moving = run.state !== IDLE;
  useEffect(() => {
    if (!moving) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [moving, run.startsAt]);

  const remainingMs = moving ? Math.max(0, run.endsAt - now) : 0;
  const elapsedMs = moving ? Math.max(0, Math.min(run.durationMs, now - run.startsAt)) : 0;

  return {
    uno,
    noun,
    // Ready-made labels, so three surfaces cannot word the same button three
    // ways — and so a gate never says "Open Curtain".
    labels: {
      open: `Open ${noun}`,
      stop: `Stop ${noun}`,
      close: `Close ${noun}`,
    },
    // state
    state: run.state,
    moving,
    opening: run.state === OPENING,
    closing: run.state === CLOSING,
    // When this run's motor command goes out. Exposed so a view can key an
    // animation on it: two runs in the same direction are two different runs,
    // and `state` alone cannot tell them apart.
    startsAt: run.startsAt,
    remainingMs,
    remainingSeconds: Math.ceil(remainingMs / 1000),
    // 0..100, for a progress bar. Guarded: durationMs is 0 when idle.
    progress: run.durationMs ? (elapsedMs / run.durationMs) * 100 : 0,
    // settings
    times,
    loadingTimes,
    reloadTimes,
    saveTimes: useCallback(async (next) => {
      // ⚠️ `singleOutput` comes from what was READ, not from the caller — a
      // dialog that forgot to pass it would write a close time to a row the
      // sliding gate does not have, and the endpoint would report success.
      const out = await saveCurtainTimes(uno, { ...next, singleOutput: times.singleOutput });
      if (out.ok) {
        setTimes((prev) => ({
          ...prev,
          openSeconds: out.openSeconds,
          closeSeconds: out.closeSeconds,
          openSet: true,
          closeSet: true,
        }));
      }
      return out;
    }, [uno, times.singleOutput]),
    // actions
    open: useCallback(
      () => openCurtain(uno, userId, times.openSeconds),
      [uno, userId, times.openSeconds],
    ),
    close: useCallback(
      () => closeCurtain(uno, userId, times.closeSeconds),
      [uno, userId, times.closeSeconds],
    ),
    stop: useCallback(() => stopCurtain(uno, userId), [uno, userId]),
  };
}
